import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import RatingStarSvg from '../../../assets/icons/icon-rating-star.svg?react';
import { rubikTypographyStyles } from '../../../shared/ui';

type RegistrationCompleteFeedbackCardVariant = 'compact' | 'preview';

type FeedbackCardMetrics = {
  cardMinHeightPx: number;
  cardRadiusPx: number;
  cardPaddingXpx: number;
  cardPaddingYpx: number;
  gapPx: number;
  starGapPx: number;
  starSizePx: number;
  titleFontSizePx: number;
};

const feedbackCardMetricsByVariant: Record<
  RegistrationCompleteFeedbackCardVariant,
  FeedbackCardMetrics
> = {
  compact: {
    cardMinHeightPx: 72,
    cardRadiusPx: 8,
    cardPaddingXpx: 15,
    cardPaddingYpx: 12,
    gapPx: 8,
    starGapPx: 9,
    starSizePx: 28,
    titleFontSizePx: 8,
  },
  preview: {
    cardMinHeightPx: 95,
    cardRadiusPx: 10,
    cardPaddingXpx: 20,
    cardPaddingYpx: 16,
    gapPx: 14,
    starGapPx: 12,
    starSizePx: 39,
    titleFontSizePx: 11,
  },
};

const FeedbackCard = styled.article.attrs({
  'data-ui': 'registration-complete-feedback-card',
})<{ $metrics: FeedbackCardMetrics }>`
  min-height: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.cardMinHeightPx)};
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.surface.base};
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.cardRadiusPx)};
  padding: ${({ $metrics, theme }) => theme.scale.px($metrics.cardPaddingYpx)}
    ${({ $metrics, theme }) => theme.scale.px($metrics.cardPaddingXpx)};
  display: grid;
  align-content: start;
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.gapPx)};
`;

const Title = styled.p<{ $metrics: FeedbackCardMetrics }>`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.titleFontSizePx)};
  font-weight: 600;
  line-height: 1.2;
`;

const StarsRow = styled.div<{ $metrics: FeedbackCardMetrics }>`
  display: flex;
  justify-content: center;
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.starGapPx)};
`;

const FeedbackStar = styled(RatingStarSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})<{ $metrics: FeedbackCardMetrics }>`
  width: ${({ $metrics, theme }) => theme.scale.px($metrics.starSizePx)};
  height: ${({ $metrics, theme }) => theme.scale.px($metrics.starSizePx)};
  display: block;
  color: ${({ theme }) => theme.colors.text.muted};

  & path {
    fill: currentColor;
  }
`;

type RegistrationCompleteFeedbackCardProps = {
  variant?: RegistrationCompleteFeedbackCardVariant;
};

export function RegistrationCompleteFeedbackCard({
  variant = 'compact',
}: RegistrationCompleteFeedbackCardProps) {
  const { t } = useTranslation();
  const metrics = feedbackCardMetricsByVariant[variant];

  return (
    <FeedbackCard $metrics={metrics}>
      <Title $metrics={metrics}>
        {t('registrationComplete.feedbackTitle')}
      </Title>
      <StarsRow $metrics={metrics}>
        {[0, 1, 2, 3, 4].map(index => (
          <FeedbackStar key={index} $metrics={metrics} />
        ))}
      </StarsRow>
    </FeedbackCard>
  );
}
